import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuth } from "../../auth/AuthContext";
import { publicApi } from "../../utils/publicApi";
import SiteHeader from "../../components/SiteHeader";
import SiteFooter from "../../components/SiteFooter";
import "./PublicCms.css";
import "./PublicPricing.css";

const FEATURE_ROWS = [
  ["allow_webhook", "mdi:webhook", "Webhooks"],
  ["allowapi", "mdi:key-variant", "API access"],
  ["allowchatbot", "mdi:robot-outline", "Chatbot"],
  ["allowbulkmsg", "mdi:bullhorn-outline", "Bulk messaging"],
  ["allowschedulemsg", "mdi:calendar-clock", "Scheduled sends"],
  ["allow_multi_instance", "mdi:layers-triple-outline", "Multi instance"],
];

const readIsFree = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return Number(u?.is_free) === 1;
  } catch {
    return false;
  }
};

const PublicPricing = () => {
  const { userAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isFreeAccount = userAuthenticated && readIsFree();

  const orderPath = !userAuthenticated
    ? "/register"
    : isFreeAccount
      ? "/dashboard"
      : "/plans";

  useEffect(() => {
    document.title = "Pricing — AlphaXCloud";
    (async () => {
      try {
        const res = await publicApi({ url: "plan/get-all" });
        if (res?.success) setPlans(Array.isArray(res.data) ? res.data : []);
        else setError(res?.msg || "Could not load plans");
      } catch {
        setError("Could not load plans");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const featuredId = useMemo(() => {
    if (!plans.length) return null;
    const paid = plans.filter((p) => Number(p.price) > 0);
    if (paid.length === 1) return paid[0].id;
    if (paid.length >= 2) {
      const mid = Math.floor((paid.length - 1) / 2);
      return paid[mid].id;
    }
    return plans[0].id;
  }, [plans]);

  return (
    <div className="public-cms pricing-page site-theme">
      <SiteHeader />

      <main className="pricing-main">
        <header className="pricing-hero">
          <div className="pricing-hero-copy">
            <p className="pricing-eyebrow">Pricing</p>
            <h1>Pick the plan that matches your volume</h1>
            <p>
              Clear limits on sessions, messages, and contacts. Free plans activate
              instantly; paid orders are confirmed from your workspace.
            </p>
          </div>
          <ul className="pricing-hero-points">
            <li>
              <Icon icon="mdi:flash-outline" width={20} />
              Activate free plans in one click
            </li>
            <li>
              <Icon icon="mdi:shield-check-outline" width={20} />
              Limits apply the moment a plan is active
            </li>
            <li>
              <Icon icon="mdi:headset" width={20} />
              Support chat for order &amp; billing help
            </li>
          </ul>
        </header>

        {loading ? (
          <p className="pricing-state">Loading plans…</p>
        ) : error ? (
          <p className="pricing-state is-error">{error}</p>
        ) : plans.length === 0 ? (
          <p className="pricing-state">No plans published yet. Check back soon.</p>
        ) : (
          <div
            className={`pricing-grid pricing-grid-${Math.min(plans.length, 3)}`}
          >
            {plans.map((plan, index) => {
              const free = Number(plan.price) <= 0;
              const featured = plan.id === featuredId;
              return (
                <article
                  key={plan.id}
                  className={[
                    "pricing-card",
                    free ? "is-free" : "",
                    featured ? "is-featured" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  {featured ? (
                    <span className="pricing-badge">Most popular</span>
                  ) : free ? (
                    <span className="pricing-badge is-free">Free start</span>
                  ) : null}

                  <div className="pricing-card-top">
                    <h2>{plan.name}</h2>
                    <p className="pricing-amount">
                      {free ? (
                        "Free"
                      ) : (
                        <>
                          <span className="pricing-currency">$</span>
                          {Number(plan.price).toLocaleString()}
                        </>
                      )}
                    </p>
                    <p className="pricing-term">
                      {plan.planexpire || "—"} days of access
                    </p>
                  </div>

                  <ul className="pricing-limits">
                    <li>
                      <Icon icon="mdi:whatsapp" width={18} />
                      <span>
                        <strong>{plan.instance}</strong> WhatsApp session
                        {Number(plan.instance) === 1 ? "" : "s"}
                      </span>
                    </li>
                    <li>
                      <Icon icon="mdi:message-text-outline" width={18} />
                      <span>
                        <strong>{Number(plan.msglimit || 0).toLocaleString()}</strong>{" "}
                        messages
                      </span>
                    </li>
                    <li>
                      <Icon icon="mdi:account-box-outline" width={18} />
                      <span>
                        <strong>
                          {Number(plan.contactlimit || 0).toLocaleString()}
                        </strong>{" "}
                        contacts
                      </span>
                    </li>
                    <li>
                      <Icon icon="mdi:file-document-outline" width={18} />
                      <span>
                        <strong>
                          {Number(plan.templetlimit || 0).toLocaleString()}
                        </strong>{" "}
                        templates
                      </span>
                    </li>
                  </ul>

                  <ul className="pricing-features">
                    {FEATURE_ROWS.map(([key, icon, label]) => {
                      const on = Number(plan[key]) === 1;
                      return (
                        <li key={key} className={on ? "is-on" : "is-off"}>
                          <Icon
                            icon={on ? "mdi:check-circle" : "mdi:close-circle-outline"}
                            width={18}
                          />
                          <Icon icon={icon} width={16} className="pricing-feat-icon" />
                          {label}
                        </li>
                      );
                    })}
                  </ul>

                  <Link
                    className={`pricing-cta${featured ? " is-featured" : ""}`}
                    to={orderPath}
                  >
                    {isFreeAccount
                      ? "Go to dashboard"
                      : userAuthenticated
                        ? free
                          ? "Activate in workspace"
                          : "Order this plan"
                        : free
                          ? "Start free"
                          : "Get started"}
                    <Icon icon="mdi:arrow-right" width={18} />
                  </Link>
                </article>
              );
            })}
          </div>
        )}

        <section className="pricing-assurance">
          <div>
            <Icon icon="mdi:swap-horizontal" width={22} />
            <h3>Upgrade anytime</h3>
            <p>Order a higher package from Plans &amp; Orders when you outgrow limits.</p>
          </div>
          <div>
            <Icon icon="mdi:account-check-outline" width={22} />
            <h3>Team-confirmed paid orders</h3>
            <p>Paid requests stay pending until payment is confirmed and the plan is applied.</p>
          </div>
          <div>
            <Icon icon="mdi:lock-outline" width={22} />
            <h3>Workspace-gated features</h3>
            <p>API, chatbot, and bulk tools follow the flags on your active plan.</p>
          </div>
        </section>

        <div className="pricing-footnote">
          <p>
            Already have an account?{" "}
            <Link
              to={
                !userAuthenticated
                  ? "/login"
                  : isFreeAccount
                    ? "/dashboard"
                    : "/plans"
              }
            >
              {!userAuthenticated
                ? "Login to order"
                : isFreeAccount
                  ? "Open dashboard"
                  : "Open Plans & Orders"}
            </Link>
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default PublicPricing;
